import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Download as DownloadIcon } from 'lucide-react';

const TuxIcon = ({ size = 32 }) => (
  <img src="/tux.svg" alt="Linux" width={size} height={size} style={{ objectFit: 'contain' }} />
);

const AppleIcon = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const WindowsIcon = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 12V6.5l8-1.1V12H3zm0 .5h8v6.6l-8-1.1V12.5zM11.5 5.3l9.5-1.3v8h-9.5V5.3zM21 12.5v7.8l-9.5-1.3V12.5H21z"/>
  </svg>
);

const platforms = [
  {
    icon: TuxIcon,
    name: 'Linux',
    description: 'Ubuntu 22.04+, Debian 12+, Fedora 38+',
    formats: ['.deb', '.AppImage'],
    recommended: true,
  },
  {
    icon: AppleIcon,
    name: 'macOS',
    description: 'macOS 13+ (Apple Silicon)',
    formats: ['.pkg'],
    recommended: false,
  },
  {
    icon: WindowsIcon,
    name: 'Windows',
    description: 'Windows 10/11 (64-bit)',
    formats: ['.exe'],
    recommended: false,
  },
];

const Download = () => {
  return (
    <div className="min-h-screen bg-page">
      <Header />

      <section className="pt-32 pb-16 px-6 relative overflow-hidden">
        <div className="absolute top-20 right-10 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: '#F97316' }} />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-foreground-primary mb-6">Download Forge Studio</h1>
          <p className="text-xl text-foreground-secondary">
            Free for individual developers. Get started in minutes.
          </p>
        </div>
      </section>

      {/* Install Script */}
      <section className="pb-12 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="glass-frosted rounded-2xl p-8 glow-subtle text-center">
            <h2 className="text-2xl font-bold text-foreground-primary mb-4">Quick Install (Linux)</h2>
            <div className="code-block text-left mb-4">
              <p><span className="text-foreground-muted">$</span> <span className="text-forge-400">curl -sSL</span> forge.vinayakrobotics.com/install <span className="text-foreground-muted">|</span> bash</p>
            </div>
            <p className="text-sm text-foreground-muted">Detects your system, installs Forge Studio, and configures ROS2 automatically.</p>
          </div>
        </div>
      </section>

      {/* Platform Cards */}
      <section className="pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-foreground-primary text-center mb-12">Choose Your Platform</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {platforms.map((p, i) => (
              <div
                key={i}
                className={`glass-frosted rounded-3xl p-8 transition-smooth glow-subtle text-center relative ${
                  p.recommended ? 'ring-2 ring-orange-500' : ''
                }`}
              >
                {p.recommended && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold"
                    style={{ background: '#F97316', color: '#0B0E13' }}
                  >
                    RECOMMENDED
                  </div>
                )}
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                  style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)' }}
                >
                  <p.icon size={32} style={{ color: '#F97316' }} />
                </div>
                <h3 className="text-2xl font-bold text-foreground-primary mb-2">{p.name}</h3>
                <p className="text-sm text-foreground-secondary mb-6">{p.description}</p>
                <div className="flex flex-wrap justify-center gap-2 mb-6">
                  {p.formats.map((f, j) => (
                    <span
                      key={j}
                      className="text-xs px-3 py-1 rounded-full"
                      style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316' }}
                    >
                      {f}
                    </span>
                  ))}
                </div>
                <button
                  className="w-full py-3 rounded-lg font-semibold transition-smooth inline-flex items-center justify-center gap-2"
                  style={{ background: p.recommended ? '#F97316' : 'transparent', color: p.recommended ? '#0B0E13' : 'var(--fg-primary)', border: p.recommended ? 'none' : '1px solid var(--border-subtle)' }}
                >
                  <DownloadIcon size={16} />
                  Download
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="glass-frosted rounded-2xl p-8 glow-subtle">
            <h3 className="text-xl font-bold text-foreground-primary mb-6">System Requirements</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'OS', value: 'Linux, macOS, or Windows' },
                { label: 'RAM', value: '4 GB minimum, 8 GB recommended' },
                { label: 'Disk', value: '500 MB for Studio' },
                { label: 'ROS2', value: 'Optional — Studio can install it' },
              ].map((req, i) => (
                <div
                  key={i}
                  className="py-3 border-b border-subtle"
                >
                  <span className="text-foreground-muted text-sm block">{req.label}</span>
                  <span className="text-foreground-primary text-sm font-medium">{req.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Download;
